import ArrowIcon from '~/assets/svg/newSvg/arrow-select.svg?react';
import styles from './CustomSelect.module.scss';
import CloseIcon from '~/assets/svg/newSvg/close-purple.svg?react';
import React, { useState } from 'react';
import cn from 'classnames';
import Option from './Option/Option';
import { SearchInput } from './SearchInput/SearchInput';
import { Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';

export interface IOption {
  id: number | string;
  name: string | JSX.Element;
  display_name?: string;
  full_name?: string;
}

interface IMultiSelectProps {
  placeholder: string;
  value: IOption[];
  disabled: boolean | undefined;
  onDeleteClick: (option: IOption) => void;
}

const MultiSelect = ({
  value,
  placeholder,
  disabled,
  onDeleteClick,
}: IMultiSelectProps) => {
  if (value?.length) {
    return (
      <ul className={styles.multiSelect}>
        {value.map(value => {
          return (
            <li key={value.id} className={styles.selectedOption}>
              {value.display_name || value.full_name || value.name}
              <button
                onClick={
                  disabled
                    ? undefined
                    : event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onDeleteClick(value);
                      }
                }
                type="button"
                className={styles.selectedOptionDelete}
              >
                <CloseIcon />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
  return <span className={styles.placeholder}>{placeholder}</span>;
};

interface ISelectProps {
  placeholder: string;
  value: IOption | IOption[] | undefined;
}

const SingleSelect = ({ placeholder, value }: ISelectProps) => {
  if (!Array.isArray(value) && value?.name) {
    return (
      <span className={styles.value}>{value?.display_name || value?.name}</span>
    );
  }
  return <span className={styles.placeholder}>{placeholder}</span>;
};

interface ICustomSelectProps {
  label?: string;
  placeholder: string;
  options: IOption[] | undefined;
  value?: IOption | IOption[] | any;
  disabled?: boolean;
  multiselect?: boolean;
  isSearchEnabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  classNameLabel?: string;
  onChange: (option: IOption | IOption[]) => void;
  onTouch?: () => void;
  hasCheckbox?: boolean;
  setIsCheckedAll?: (value: boolean) => void;
  checkboxLabel?: string;
}

const CustomSelect = ({
  label,
  placeholder = 'Select option',
  options,
  value,
  disabled,
  multiselect,
  isSearchEnabled,
  required,
  error,
  className,
  classNameLabel,
  onTouch,
  onChange,
  hasCheckbox = false,
  setIsCheckedAll,
  checkboxLabel,
}: ICustomSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isChecked, setIsChecked] = useState(false);
  const [prevSelectedItems, setPrevSelectedItems] = useState(value);
  const handleToggleSelect = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (option: IOption) => {
    if (multiselect && Array.isArray(value)) {
      const newValue = value.find((item: IOption) => item.id === option.id)
        ? value.filter((item: IOption) => item.id !== option.id)
        : [...value, option];
      onChange(newValue);
      setPrevSelectedItems(newValue);
    } else {
      onChange(option);
      setIsOpen(false);
    }
  };

  const handleCloseDropdown = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleCheck = (checked: boolean) => {
    setPrevSelectedItems(value);
    setIsChecked(checked);
    if (setIsCheckedAll) {
      setIsCheckedAll(checked);
    }
    if (checked) {
      onChange(options ?? []);
    } else {
      onChange(prevSelectedItems);
    }
  };

  const handleDiscard = () => {
    setIsChecked(false);
    if (setIsCheckedAll) {
      setIsCheckedAll(false);
    }
    onChange([]);
  };

  return (
    <div
      className={cn(
        styles.customSelect,
        multiselect ? styles.customSelectMultiselect : undefined,
        isOpen ? styles.customSelectOpen : undefined,
        disabled ? styles.customSelectDisabled : undefined,
        className
      )}
      onBlur={event => {
        handleCloseDropdown(event);
        if (onTouch) {
          onTouch();
        }
      }}
    >
      {label ? (
        <span className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </span>
      ) : (
        ''
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={disabled ? undefined : handleToggleSelect}
        onKeyDown={disabled ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') handleToggleSelect(); }}
        className={cn(
          styles.customSelectLabel,
          isOpen ? styles.customSelectLabelOpen : undefined,
          classNameLabel
        )}
      >
        {multiselect && Array.isArray(value) ? (
          <MultiSelect
            value={value}
            onDeleteClick={handleSelectOption}
            placeholder={placeholder}
            disabled={disabled}
          />
        ) : (
          <SingleSelect placeholder={placeholder} value={value} />
        )}
        <ArrowIcon
          className={cn(
            styles.arrowIcon,
            isOpen ? styles.arrowIconOpen : undefined
          )}
        />
      </div>
      {isOpen ? (
        <ul className={styles.options}>
          {isSearchEnabled && (
            <div className={styles.searchWrapper}>
              <SearchInput
                value={searchValue}
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
                onClearPress={() => {
                  setSearchValue('');
                }}
              />
            </div>
          )}

          {(options ?? [])
            .filter(option => {
              if (typeof option.name === 'string' && searchValue) {
                return option.name
                  .toLowerCase()
                  .includes(searchValue.toLowerCase());
              }
              return option;
            })
            .map(option => (
              <Option
                key={option.id}
                onClick={() => handleSelectOption(option)}
                name={option.display_name || option.full_name || option.name}
                isSelected={
                  Array.isArray(value)
                    ? !!value.find((item: IOption) => item.id === option.id)
                    : value?.id === option.id
                }
              />
            ))}
        </ul>
      ) : (
        ''
      )}
      {error && <span className={styles.error}>{error}</span>}
      {hasCheckbox && multiselect && (
        <div className={styles.checkboxWrapper}>
          <label>
            <Checkbox
              checked={isChecked}
              onChange={e => handleCheck(e.target.checked)}
            />
            <span className={styles.checkboxLabel}>
              {checkboxLabel || t('all_sections')}
            </span>
          </label>
          {!value ||
            (Array.isArray(value) && !!value.length && (
              <button
                type="button"
                onClick={handleDiscard}
                className={styles.discardBtn}
              >
                {t('discard')}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomSelect);
